import { apiRequest } from "@/shared/lib/apiClient";

export type AdminDashboardApiResponse = {
  admin_name: string;
  header: {
    role_badge: string;
    display_date: string;
    total_invoices: number;
    admissions: number;
  };
  summary: {
    total_invoices: number;
    in_review: number;
    approved: number;
    admissions: number;
    admissions_today: number;
    admissions_total: number;
    admissions_en_cours: number;
  };
  invoice_distribution: Array<{ key: string; value: number }>;
  system_stats: {
    hospitals: number;
    companies: number;
    auditors: number;
    users: number;
    validation_rate: string;
  };
  recent_submitted: Array<{
    id: number;
    reference: string;
    label: string;
    hospital: string;
    company: string;
    amount_usd: number;
    status: string;
    created_at: string | null;
  }>;
  top_hospitals_year?: number;
  top_hospitals?: Array<{
    id: number;
    name: string;
    admissions: number;
  }>;
};

export async function fetchAdminDashboard(): Promise<AdminDashboardApiResponse> {
  return apiRequest<AdminDashboardApiResponse>("/admin/dashboard", { auth: true });
}

export type AdminUsersApiResponse = {
  total: number;
  users: Array<{
    id: number;
    name: string;
    email: string;
    role: string;
    created_at: string | null;
  }>;
};

export async function fetchAdminUsers(query: {
  role?: string;
  name?: string;
  email?: string;
} = {}): Promise<AdminUsersApiResponse> {
  const params = new URLSearchParams();
  if (query.role) params.set("role", query.role);
  if (query.name) params.set("name", query.name);
  if (query.email) params.set("email", query.email);
  const qs = params.toString();
  return apiRequest<AdminUsersApiResponse>(`/admin/users${qs ? `?${qs}` : ""}`, { auth: true });
}

export type AdminUserPayload = {
  username: string;
  email: string;
  role: string;
  password?: string;
};

export type AdminUser = AdminUsersApiResponse["users"][number];

export async function fetchAdminUser(id: number): Promise<AdminUser> {
  const response = await apiRequest<{ user: AdminUser }>(`/admin/users/${id}`, { auth: true });
  return response.user;
}

export async function createAdminUser(payload: AdminUserPayload): Promise<AdminUser> {
  const response = await apiRequest<{ user: AdminUser; message?: string }>("/admin/users", {
    method: "POST",
    auth: true,
    body: payload,
  });
  return response.user;
}

export async function updateAdminUser(id: number, payload: AdminUserPayload): Promise<AdminUser> {
  const response = await apiRequest<{ user: AdminUser; message?: string }>(`/admin/users/${id}`, {
    method: "PUT",
    auth: true,
    body: payload,
  });
  return response.user;
}

export async function deleteAdminUser(id: number): Promise<void> {
  await apiRequest<{ message?: string }>(`/admin/users/${id}`, {
    method: "DELETE",
    auth: true,
  });
}

export type AdminEmployeeRow = {
  id: number;
  name: string;
  system_id: string;
  company_matricule: string;
  company: string;
  company_id: number;
  province: string;
  branch: string;
  status: "active" | "inactive";
  photo: string;
};

export async function fetchAdminEmployees(query: {
  search?: string;
  company_id?: number;
  province?: string;
  branch?: string;
  system_id?: string;
  status?: string;
} = {}): Promise<{ total: number; employees: AdminEmployeeRow[] }> {
  const params = new URLSearchParams();
  if (query.search) params.set("search", query.search);
  if (query.company_id) params.set("company_id", String(query.company_id));
  if (query.province) params.set("province", query.province);
  if (query.branch) params.set("branch", query.branch);
  if (query.system_id) params.set("system_id", query.system_id);
  if (query.status) params.set("status", query.status);
  const qs = params.toString();
  return apiRequest<{ total: number; employees: AdminEmployeeRow[] }>(
    `/admin/employees${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export type AdminEmployeeDetail = AdminEmployeeRow & {
  birthdate?: string | null;
  gender?: string | null;
  phone?: string | null;
  email?: string | null;
  city?: string | null;
  position?: string | null;
  hospital?: string;
  hospital_id?: number | null;
  primary_hospital_id?: number | null;
  hospital_ids?: number[];
  hospitals?: Array<{ hospital_id: number; name: string; is_primary: boolean }>;
  family_members?: Array<{
    id: number;
    name: string;
    relationship: string;
    birthdate: string | null;
    gender: string | null;
    photo?: string | null;
    status?: "active" | "inactive";
  }>;
};

export async function fetchAdminEmployeeFamilyMembers(
  employeeId: number,
): Promise<Array<{ id: number; name: string; relationship: string }>> {
  const detail = await fetchAdminEmployee(employeeId);
  return (detail.family_members ?? []).map((member) => ({
    id: member.id,
    name: member.name,
    relationship: member.relationship,
  }));
}

export type AdminEmployeeLookupResult = {
  employee: {
    id: number;
    matricule: string;
    employee_name: string;
    company_id: number;
    company_name: string;
  };
  family_members: Array<{ id: number; name: string; relationship: string }>;
};

/** Lookup exact par matricule / ID (pas de recherche floue). */
export async function lookupAdminEmployee(params: {
  matricule?: string;
  employee_id?: number;
  company_id?: number;
}): Promise<AdminEmployeeLookupResult> {
  const search = new URLSearchParams();
  if (params.matricule) search.set("matricule", params.matricule.trim());
  if (params.employee_id) search.set("employee_id", String(params.employee_id));
  if (params.company_id) search.set("company_id", String(params.company_id));
  return apiRequest<AdminEmployeeLookupResult>(`/admin/employees/lookup?${search.toString()}`, {
    auth: true,
  });
}

export async function fetchAdminEmployee(id: number): Promise<AdminEmployeeDetail> {
  const response = await apiRequest<{ employee: AdminEmployeeDetail }>(`/admin/employees/${id}`, {
    auth: true,
  });
  return response.employee;
}

export async function fetchAdminEmployeeMeta(): Promise<{
  companies: Array<{ id: number; name: string }>;
}> {
  return apiRequest<{ companies: Array<{ id: number; name: string }> }>("/admin/employees/meta", {
    auth: true,
  });
}

export async function fetchAdminNetworkHospitals(
  companyId: number,
): Promise<Array<{ id: number; name: string }>> {
  const response = await apiRequest<{ hospitals: Array<{ id: number; name: string }> }>(
    `/admin/employees/network-hospitals?company_id=${companyId}`,
    { auth: true },
  );
  return response.hospitals ?? [];
}

export async function createAdminEmployee(
  payload: Record<string, unknown>,
  photo?: File | null,
  familyPhotos?: Array<File | null | undefined>,
): Promise<AdminEmployeeDetail> {
  const hasFamilyPhotos = (familyPhotos ?? []).some((f) => f instanceof File);
  if (photo || hasFamilyPhotos) {
    const form = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (key === "family_members" || key === "hospital_ids") {
        form.append(key, JSON.stringify(value));
        return;
      }
      form.append(key, String(value));
    });
    if (photo) form.append("photo", photo);
    (familyPhotos ?? []).forEach((file, index) => {
      if (file instanceof File) form.append(`family_photos[${index}]`, file);
    });
    const response = await apiRequest<{ employee: AdminEmployeeDetail }>("/admin/employees", {
      method: "POST",
      auth: true,
      body: form,
    });
    return response.employee;
  }

  const response = await apiRequest<{ employee: AdminEmployeeDetail }>("/admin/employees", {
    method: "POST",
    auth: true,
    body: payload,
  });
  return response.employee;
}

export async function updateAdminEmployee(
  id: number,
  payload: Record<string, unknown>,
  photo?: File | null,
  familyPhotos?: Array<File | null | undefined>,
): Promise<AdminEmployeeDetail> {
  const hasFamilyPhotos = (familyPhotos ?? []).some((f) => f instanceof File);
  if (photo || hasFamilyPhotos) {
    const form = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (key === "family_members" || key === "hospital_ids") {
        form.append(key, JSON.stringify(value));
        return;
      }
      form.append(key, String(value));
    });
    if (photo) form.append("photo", photo);
    (familyPhotos ?? []).forEach((file, index) => {
      if (file instanceof File) form.append(`family_photos[${index}]`, file);
    });
    const response = await apiRequest<{ employee: AdminEmployeeDetail }>(`/admin/employees/${id}`, {
      method: "POST",
      auth: true,
      body: form,
    });
    return response.employee;
  }

  const response = await apiRequest<{ employee: AdminEmployeeDetail }>(`/admin/employees/${id}`, {
    method: "PUT",
    auth: true,
    body: payload,
  });
  return response.employee;
}

export type AdminBeneficiaryRow = {
  id: string;
  record_id: number;
  type: "employee" | "dependant";
  name: string;
  relation: string;
  birth_date: string;
  age: string;
  gender: string;
  employee: string;
  matricule: string;
  company_matricule: string;
  company: string;
  photo: string;
  status: "active" | "inactive";
};

export async function fetchAdminBeneficiaries(search?: string): Promise<{
  total: number;
  beneficiaries: AdminBeneficiaryRow[];
}> {
  const params = new URLSearchParams();
  if (search) params.set("search", search);
  const qs = params.toString();
  return apiRequest<{ total: number; beneficiaries: AdminBeneficiaryRow[] }>(
    `/admin/beneficiaries${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export type AdminProvider = {
  id: number;
  commercial_name: string;
  type: string;
  type_raw: string;
  city: string;
  province: string;
  phone: string;
  email: string;
  status: "active" | "inactive";
  status_raw: string;
  agreement_number: string;
};

export type AdminProvidersQuery = {
  type?: string;
  status?: string;
  ville?: string;
  q?: string;
};

export async function fetchAdminProviders(
  query: AdminProvidersQuery = {},
): Promise<{ total: number; providers: AdminProvider[] }> {
  const params = new URLSearchParams();
  if (query.type) params.set("type", query.type);
  if (query.status) params.set("status", query.status);
  if (query.ville) params.set("ville", query.ville);
  if (query.q) params.set("q", query.q);
  const qs = params.toString();
  return apiRequest<{ total: number; providers: AdminProvider[] }>(
    `/admin/providers${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export type AdminProviderDetail = AdminProvider & {
  address: string;
  postal_code: string;
  website: string;
  contact_name: string;
  contact_phone: string;
  contact_email: string;
  rccm: string;
  id_nat: string;
  description: string;
  specialties: string;
};

export type AdminProviderPayload = {
  commercial_name: string;
  type: string;
  status?: string;
  description?: string;
  address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  phone?: string;
  email?: string;
  website?: string;
  contact_name?: string;
  contact_phone?: string;
  contact_email?: string;
  agreement_number?: string;
  rccm?: string;
  id_nat?: string;
  specialties?: string;
};

export async function fetchAdminProvider(id: number): Promise<AdminProviderDetail> {
  const response = await apiRequest<{ provider: AdminProviderDetail }>(`/admin/providers/${id}`, {
    auth: true,
  });
  return response.provider;
}

export async function createAdminProvider(payload: AdminProviderPayload): Promise<AdminProviderDetail> {
  const response = await apiRequest<{ provider: AdminProviderDetail; message?: string }>(
    "/admin/providers",
    { method: "POST", auth: true, body: payload },
  );
  return response.provider;
}

export async function updateAdminProvider(
  id: number,
  payload: AdminProviderPayload,
): Promise<AdminProviderDetail> {
  const response = await apiRequest<{ provider: AdminProviderDetail; message?: string }>(
    `/admin/providers/${id}`,
    { method: "PUT", auth: true, body: payload },
  );
  return response.provider;
}

export async function updateAdminProviderStatus(
  id: number,
  status: "active" | "inactive",
): Promise<AdminProvider> {
  const response = await apiRequest<{ provider: AdminProvider; message?: string }>(
    `/admin/providers/${id}/status`,
    {
      method: "PATCH",
      auth: true,
      body: { status },
    },
  );
  return response.provider;
}

export type AdminConvention = {
  id: number;
  code: string;
  name: string;
  company_id: number;
  company: string;
  provider_count: number;
  provider_ids?: number[];
  type: "forfaitaire" | "tiers_payant" | "remboursement" | "mixte" | string;
  validity_start: string;
  validity_end: string;
  date_debut: string;
  date_fin: string;
  budget: number;
  consumption_pct: number;
  status: "active" | "expiring" | "expired" | "draft" | "suspended" | "cancelled" | string;
  status_raw: string;
};

export type AdminConventionDetail = AdminConvention & {
  description: string;
  special_conditions: string;
  coverage_unit?: "personne" | "famille" | "mixte" | string;
  budget_periodicity?: "trimestriel" | "semestriel" | "annuel" | string;
  individual_ceiling: number | null;
  daily_ceiling?: number | null;
  monthly_ceiling?: number | null;
  annual_ceiling?: number | null;
  family_daily_ceiling?: number | null;
  family_monthly_ceiling?: number | null;
  family_annual_ceiling?: number | null;
  monthly_package_amount: number | null;
  copay_pct: number;
  global_budget_alert_threshold: number;
  individual_alert_threshold: number | null;
  child_age_limit: number;
  student_child_age_limit: number;
  spouse_age_limit: number | null;
  max_dependents_per_employee: number;
  covers_spouse: boolean;
  covers_children: boolean;
  covers_parents: boolean;
  covers_other: boolean;
  provider_ids: number[];
  providers: Array<{
    id: number;
    commercial_name: string;
    type: string;
    city: string;
    forfait: number | null;
    client_forfait: number | null;
  }>;
  consumption?: AdminConventionConsumption | null;
};

export type AdminConventionConsumptionInvoice = {
  id: number;
  reference: string;
  provider: string;
  nature: string;
  nature_label: string;
  amount_usd: number;
  status: string;
  period_start: string | null;
  period_end: string | null;
  period_label: string;
  created_at: string | null;
};

export type AdminConventionConsumption = {
  period_key: string;
  period_start: string;
  period_end: string;
  budget: number;
  consumed: number;
  consumed_forfait_mensuel: number;
  consumed_hors_forfait: number;
  remaining: number | null;
  consumption_pct: number | null;
  status: string;
  invoice_count: number;
  invoices: AdminConventionConsumptionInvoice[];
};

export type AdminConventionPayload = {
  title: string;
  company_id: number;
  treatment_type: string;
  start_date: string;
  end_date: string;
  description?: string;
  special_conditions?: string;
  coverage_unit?: "personne" | "famille" | "mixte";
  budget_periodicity?: "trimestriel" | "semestriel" | "annuel";
  total_budget?: number | null;
  individual_ceiling?: number | null;
  daily_ceiling?: number | null;
  monthly_ceiling?: number | null;
  annual_ceiling?: number | null;
  family_daily_ceiling?: number | null;
  family_monthly_ceiling?: number | null;
  family_annual_ceiling?: number | null;
  monthly_package_amount?: number | null;
  copay_pct?: number | null;
  global_budget_alert_threshold?: number | null;
  individual_alert_threshold?: number | null;
  child_age_limit?: number | null;
  student_child_age_limit?: number | null;
  spouse_age_limit?: number | null;
  max_dependents_per_employee?: number | null;
  covered_dependent_types?: string[];
  provider_ids: number[];
  provider_forfaits?: Record<string, number | null>;
  provider_client_forfaits?: Record<string, number | null>;
};

export type AdminConventionMeta = {
  companies: Array<{ id: number; name: string }>;
  providers: Array<{ id: number; commercial_name: string; type: string; city: string }>;
};

export type AdminConventionsQuery = {
  status?: string;
  company_id?: number;
  provider_id?: number;
};

export async function fetchAdminConventions(
  query: AdminConventionsQuery = {},
): Promise<{ total: number; conventions: AdminConvention[] }> {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.company_id) params.set("company_id", String(query.company_id));
  if (query.provider_id) params.set("provider_id", String(query.provider_id));
  const qs = params.toString();
  return apiRequest<{ total: number; conventions: AdminConvention[] }>(
    `/admin/conventions${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function fetchAdminConventionsMeta(): Promise<AdminConventionMeta> {
  return apiRequest<AdminConventionMeta>("/admin/conventions/meta", { auth: true });
}

export async function fetchAdminConvention(id: number): Promise<AdminConventionDetail> {
  const response = await apiRequest<{ convention: AdminConventionDetail }>(
    `/admin/conventions/${id}`,
    { auth: true },
  );
  return response.convention;
}

export type AdminBudgetConsumptionItem = {
  convention_id: number;
  convention_code: string;
  convention_name: string;
  company_id: number;
  company_name: string;
  type_traitement: string;
  periodicity: string;
  period_key: string;
  period_start: string;
  period_end: string;
  budget: number;
  budget_forfait?: number | null;
  budget_hors_forfait?: number | null;
  consumed: number;
  consumed_forfait_mensuel: number;
  consumed_hors_forfait: number;
  qty_total: number;
  qty_forfait: number;
  qty_hors_forfait: number;
  remaining: number | null;
  consumption_pct: number | null;
  consumption_pct_forfait?: number | null;
  consumption_pct_hors_forfait?: number | null;
  alert_threshold_pct: number;
  status: "ok" | "alert" | "depleted" | string;
};

export type AdminBudgetRevision = {
  id: number;
  convention_id: number;
  period_key: string;
  budget_nature: string;
  revision_type: string;
  previous_amount: number | null;
  new_amount: number;
  delta_amount: number;
  effective_from: string | null;
  nb_familles_before: number | null;
  nb_familles_after: number | null;
  nb_familles_delta: number | null;
  reason: string | null;
  created_at: string;
};

export type AdminBudgetRevisionSuggestion = {
  current_amount: number;
  suggested_delta: number;
  suggested_new_amount: number;
  nb_familles_before: number | null;
  nb_familles_after: number | null;
  nb_familles_delta: number | null;
  detail: string | null;
  effective_from: string;
};

export type AdminBudgetConsumptionMeta = {
  companies: Array<{ id: number; name: string }>;
  conventions: Array<{
    id: number;
    name: string;
    company_id: number;
    company_name: string;
    periodicity: string;
  }>;
  period_options: Array<{ value: string; label: string }>;
  periodicities: Array<{ value: string; label: string }>;
};

export async function fetchAdminBudgetConsumption(query: {
  company_id?: number;
  convention_id?: number;
  period_key?: string;
  as_of?: string;
} = {}): Promise<{ total: number; items: AdminBudgetConsumptionItem[] }> {
  const params = new URLSearchParams();
  if (query.company_id) params.set("company_id", String(query.company_id));
  if (query.convention_id) params.set("convention_id", String(query.convention_id));
  if (query.period_key) params.set("period_key", query.period_key);
  if (query.as_of) params.set("as_of", query.as_of);
  const qs = params.toString();
  return apiRequest(`/admin/budget-consumption${qs ? `?${qs}` : ""}`, { auth: true });
}

export async function fetchAdminBudgetConsumptionMeta(query: {
  periodicity?: string;
} = {}): Promise<AdminBudgetConsumptionMeta> {
  const params = new URLSearchParams();
  if (query.periodicity) params.set("periodicity", query.periodicity);
  const qs = params.toString();
  return apiRequest(`/admin/budget-consumption/meta${qs ? `?${qs}` : ""}`, { auth: true });
}

export async function updateAdminBudgetPeriod(
  conventionId: number,
  payload: {
    period_key: string;
    budget_amount: number;
    budget_nature?: string;
    reason?: string;
  },
): Promise<{ message: string; item: AdminBudgetConsumptionItem; revision?: AdminBudgetRevision }> {
  return apiRequest(`/admin/budget-consumption/${conventionId}`, {
    method: "PUT",
    auth: true,
    body: payload,
  });
}

export async function fetchAdminBudgetRevisions(
  conventionId: number,
  periodKey?: string,
): Promise<{ items: AdminBudgetRevision[] }> {
  const params = new URLSearchParams();
  if (periodKey) params.set("period_key", periodKey);
  const qs = params.toString();
  return apiRequest(`/admin/budget-consumption/${conventionId}/revisions${qs ? `?${qs}` : ""}`, {
    auth: true,
  });
}

export async function suggestAdminBudgetRevision(
  conventionId: number,
  payload: { period_key: string; effective_from: string; budget_nature?: string },
): Promise<{ suggestion: AdminBudgetRevisionSuggestion }> {
  const params = new URLSearchParams({
    period_key: payload.period_key,
    effective_from: payload.effective_from,
  });
  if (payload.budget_nature) params.set("budget_nature", payload.budget_nature);
  return apiRequest(`/admin/budget-consumption/${conventionId}/suggest?${params.toString()}`, {
    auth: true,
  });
}

export async function createAdminBudgetRevision(
  conventionId: number,
  payload: {
    period_key: string;
    budget_nature?: string;
    revision_type?: string;
    new_amount: number;
    effective_from?: string;
    nb_familles_before?: number;
    nb_familles_after?: number;
    reason?: string;
  },
): Promise<{ message: string; item: AdminBudgetConsumptionItem; revision: AdminBudgetRevision }> {
  return apiRequest(`/admin/budget-consumption/${conventionId}/revisions`, {
    method: "POST",
    auth: true,
    body: payload,
  });
}

export async function createAdminConvention(
  payload: AdminConventionPayload,
): Promise<AdminConventionDetail> {
  const response = await apiRequest<{ convention: AdminConventionDetail; message?: string }>(
    "/admin/conventions",
    { method: "POST", auth: true, body: payload },
  );
  return response.convention;
}

export async function updateAdminConvention(
  id: number,
  payload: AdminConventionPayload,
): Promise<AdminConventionDetail> {
  const response = await apiRequest<{ convention: AdminConventionDetail; message?: string }>(
    `/admin/conventions/${id}`,
    { method: "PUT", auth: true, body: payload },
  );
  return response.convention;
}

export type AdminBranch = {
  id: number;
  name: string;
  province: string;
  address: string;
  phone: string;
  email: string;
  status: "active" | "inactive";
};

export type AdminBranchPayload = {
  name: string;
  province?: string;
  address?: string;
  phone?: string;
  email?: string;
  status?: string;
};

export type AdminBranchesQuery = {
  status?: string;
  province?: string;
  q?: string;
};

export async function fetchAdminBranches(
  query: AdminBranchesQuery = {},
): Promise<{ total: number; branches: AdminBranch[] }> {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.province) params.set("province", query.province);
  if (query.q) params.set("q", query.q);
  const qs = params.toString();
  return apiRequest<{ total: number; branches: AdminBranch[] }>(
    `/admin/branches${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function fetchAdminBranch(id: number): Promise<AdminBranch> {
  const response = await apiRequest<{ branch: AdminBranch }>(`/admin/branches/${id}`, {
    auth: true,
  });
  return response.branch;
}

export async function createAdminBranch(payload: AdminBranchPayload): Promise<AdminBranch> {
  const response = await apiRequest<{ branch: AdminBranch; message?: string }>("/admin/branches", {
    method: "POST",
    auth: true,
    body: payload,
  });
  return response.branch;
}

export async function updateAdminBranch(
  id: number,
  payload: AdminBranchPayload,
): Promise<AdminBranch> {
  const response = await apiRequest<{ branch: AdminBranch; message?: string }>(
    `/admin/branches/${id}`,
    { method: "PUT", auth: true, body: payload },
  );
  return response.branch;
}

export async function updateAdminBranchStatus(
  id: number,
  status: "active" | "inactive",
): Promise<AdminBranch> {
  const response = await apiRequest<{ branch: AdminBranch; message?: string }>(
    `/admin/branches/${id}/status`,
    { method: "PATCH", auth: true, body: { status } },
  );
  return response.branch;
}

export async function deleteAdminBranch(id: number): Promise<void> {
  await apiRequest<{ message?: string }>(`/admin/branches/${id}`, {
    method: "DELETE",
    auth: true,
  });
}

export type AdminPackage = {
  id: number;
  code: string;
  name: string;
  hospital_id: number;
  company_id: number;
  hospital: string;
  company: string;
  monthly_rate: number;
  monthly_rate_label: string;
  start_date: string;
  end_date: string;
  period_start: string;
  period_end: string;
  included_acts: number;
  excluded_acts: number;
  status: "draft" | "active" | "suspended" | "expired" | string;
};

export type AdminPackageDetail = AdminPackage & {
  description: string;
  created_at?: string | null;
  included_act_ids: number[];
  excluded_act_ids?: number[];
  acts: Array<{
    id: number;
    code: string;
    label: string;
    cost: number | null;
    is_included: boolean;
    quantity_limit?: number | null;
    time_period?: string | null;
    notes?: string | null;
  }>;
};

export type AdminPackagePayload = {
  package_name: string;
  hospital_id: number;
  company_id: number;
  monthly_rate: number;
  start_date: string;
  end_date: string;
  description?: string;
  status?: string;
  included_act_ids?: number[];
  limits?: Record<number, { quantity?: number; period?: string; notes?: string }>;
};

export type AdminPackageMeta = {
  hospitals: Array<{ id: number; name: string }>;
  companies: Array<{ id: number; name: string }>;
};

export type AdminPackageAct = {
  id: number;
  code: string;
  label: string;
  cost: number | null;
};

export async function fetchAdminPackages(query: {
  status?: string;
  hospital_id?: number;
  company_id?: number;
} = {}): Promise<{ total: number; packages: AdminPackage[] }> {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.hospital_id) params.set("hospital_id", String(query.hospital_id));
  if (query.company_id) params.set("company_id", String(query.company_id));
  const qs = params.toString();
  return apiRequest<{ total: number; packages: AdminPackage[] }>(
    `/admin/packages${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function fetchAdminPackagesMeta(): Promise<AdminPackageMeta> {
  return apiRequest<AdminPackageMeta>("/admin/packages/meta", { auth: true });
}

export async function fetchAdminPackageHospitalActs(
  hospitalId: number,
): Promise<AdminPackageAct[]> {
  const response = await apiRequest<{ acts: AdminPackageAct[] }>(
    `/admin/packages/hospital-acts?hospital_id=${hospitalId}`,
    { auth: true },
  );
  return response.acts ?? [];
}

export async function createAdminPackage(
  payload: AdminPackagePayload,
): Promise<AdminPackageDetail> {
  const response = await apiRequest<{ package: AdminPackageDetail; message?: string }>(
    "/admin/packages",
    { method: "POST", auth: true, body: payload },
  );
  return response.package;
}

export async function fetchAdminPackage(id: number): Promise<AdminPackageDetail> {
  const response = await apiRequest<{ package: AdminPackageDetail }>(
    `/admin/packages/${id}`,
    { auth: true },
  );
  return response.package;
}

export type AdminPackageUpdatePayload = {
  package_name: string;
  monthly_rate: number;
  start_date: string;
  end_date: string;
  description?: string;
  status?: string;
  included_act_ids?: number[];
  limits?: Record<number, { quantity?: number; period?: string; notes?: string }>;
};

export async function updateAdminPackage(
  id: number,
  payload: AdminPackageUpdatePayload,
): Promise<AdminPackageDetail> {
  const response = await apiRequest<{ package: AdminPackageDetail; message?: string }>(
    `/admin/packages/${id}`,
    { method: "PUT", auth: true, body: payload },
  );
  return response.package;
}

export type AdminMedicalAct = {
  id: number;
  code: string;
  name: string;
  label: string;
  specialty: string;
  category: string;
  description: string;
  reference_price: number | null;
};

export type AdminMedicalActPayload = {
  code: string;
  name?: string;
  label?: string;
  category: string;
  specialty?: string;
  description?: string;
  reference_price?: number | null;
};

export async function fetchAdminMedicalActs(query: {
  category?: string;
  q?: string;
} = {}): Promise<{ total: number; medical_acts: AdminMedicalAct[] }> {
  const params = new URLSearchParams();
  if (query.category) params.set("category", query.category);
  if (query.q) params.set("q", query.q);
  const qs = params.toString();
  return apiRequest<{ total: number; medical_acts: AdminMedicalAct[] }>(
    `/admin/medical-acts${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function fetchAdminMedicalAct(id: number): Promise<AdminMedicalAct> {
  const response = await apiRequest<{ medical_act: AdminMedicalAct }>(
    `/admin/medical-acts/${id}`,
    { auth: true },
  );
  return response.medical_act;
}

export async function createAdminMedicalAct(
  payload: AdminMedicalActPayload,
): Promise<AdminMedicalAct> {
  const response = await apiRequest<{ medical_act: AdminMedicalAct; message?: string }>(
    "/admin/medical-acts",
    { method: "POST", auth: true, body: payload },
  );
  return response.medical_act;
}

export async function updateAdminMedicalAct(
  id: number,
  payload: AdminMedicalActPayload,
): Promise<AdminMedicalAct> {
  const response = await apiRequest<{ medical_act: AdminMedicalAct; message?: string }>(
    `/admin/medical-acts/${id}`,
    { method: "PUT", auth: true, body: payload },
  );
  return response.medical_act;
}

export async function deleteAdminMedicalAct(id: number): Promise<void> {
  await apiRequest<{ message?: string }>(`/admin/medical-acts/${id}`, {
    method: "DELETE",
    auth: true,
  });
}

export type AdminDiagnosis = {
  id: number;
  code: string;
  name: string;
  label: string;
  type: string;
  description: string;
};

export type AdminDiagnosisPayload = {
  code: string;
  name?: string;
  label?: string;
  type: string;
  description?: string;
};

export async function fetchAdminDiagnoses(query: {
  type?: string;
  q?: string;
} = {}): Promise<{ total: number; diagnoses: AdminDiagnosis[] }> {
  const params = new URLSearchParams();
  if (query.type) params.set("type", query.type);
  if (query.q) params.set("q", query.q);
  const qs = params.toString();
  return apiRequest<{ total: number; diagnoses: AdminDiagnosis[] }>(
    `/admin/diagnoses${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function fetchAdminDiagnosis(id: number): Promise<AdminDiagnosis> {
  const response = await apiRequest<{ diagnosis: AdminDiagnosis }>(
    `/admin/diagnoses/${id}`,
    { auth: true },
  );
  return response.diagnosis;
}

export async function createAdminDiagnosis(
  payload: AdminDiagnosisPayload,
): Promise<AdminDiagnosis> {
  const response = await apiRequest<{ diagnosis: AdminDiagnosis; message?: string }>(
    "/admin/diagnoses",
    { method: "POST", auth: true, body: payload },
  );
  return response.diagnosis;
}

export async function updateAdminDiagnosis(
  id: number,
  payload: AdminDiagnosisPayload,
): Promise<AdminDiagnosis> {
  const response = await apiRequest<{ diagnosis: AdminDiagnosis; message?: string }>(
    `/admin/diagnoses/${id}`,
    { method: "PUT", auth: true, body: payload },
  );
  return response.diagnosis;
}

export async function deleteAdminDiagnosis(id: number): Promise<void> {
  await apiRequest<{ message?: string }>(`/admin/diagnoses/${id}`, {
    method: "DELETE",
    auth: true,
  });
}

export type AdminHospitalActsSummary = {
  id: number;
  hospital: string;
  act_count: number;
  provider_type?: string;
};

export async function fetchAdminHospitalActsSummaries(query?: {
  has_acts?: boolean | null;
}): Promise<{
  total: number;
  hospitals: AdminHospitalActsSummary[];
}> {
  const params = new URLSearchParams();
  if (query?.has_acts === true) params.set("has_acts", "1");
  if (query?.has_acts === false) params.set("has_acts", "0");
  const qs = params.toString();
  return apiRequest<{ total: number; hospitals: AdminHospitalActsSummary[] }>(
    `/admin/hospital-acts${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export type AdminHospitalMedicalAct = {
  id: number;
  hospital_id: number;
  code: string;
  label: string;
  cost: number;
  care_type: string;
  care_category: string;
  delay_days?: number;
};

export async function fetchAdminHospitalActs(
  hospitalId: number,
  query: { q?: string } = {},
): Promise<{ hospital_id: number; total: number; medical_acts: AdminHospitalMedicalAct[] }> {
  const params = new URLSearchParams();
  if (query.q) params.set("q", query.q);
  const qs = params.toString();
  return apiRequest<{
    hospital_id: number;
    total: number;
    medical_acts: AdminHospitalMedicalAct[];
  }>(`/admin/hospital-acts/${hospitalId}${qs ? `?${qs}` : ""}`, { auth: true });
}

export type AdminActsReviewStats = {
  toValidate: number;
  classified: number;
  manuallyValidated: number;
  lowConfidence: number;
  unclassified: number;
};

export type AdminActsReviewAct = {
  id: number;
  hospital_id: number;
  code: string;
  label: string;
  libelle?: string;
  care_category: string;
  care_category_source?: string;
  care_category_confidence?: number | null;
  filter_status?: string;
  hospital?: string;
};

export async function fetchAdminActsReview(query: {
  hospital_id?: number;
  filter?: string;
  q?: string;
} = {}): Promise<{ stats: AdminActsReviewStats; medical_acts: AdminActsReviewAct[] }> {
  const params = new URLSearchParams();
  if (query.hospital_id) params.set("hospital_id", String(query.hospital_id));
  if (query.filter) params.set("filter", query.filter);
  if (query.q) params.set("q", query.q);
  const qs = params.toString();
  return apiRequest<{ stats: AdminActsReviewStats; medical_acts: AdminActsReviewAct[] }>(
    `/admin/hospital-acts/review${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function classifyAdminHospitalActs(
  hospitalId: number,
  options: { force?: boolean; limit?: number } = {},
): Promise<{
  classified: number;
  skipped: number;
  ai?: number;
  mode?: string;
  gemini_available?: boolean;
  truncated?: boolean;
  message?: string;
}> {
  return apiRequest(
    `/admin/hospital-acts/${hospitalId}/classify`,
    { method: "POST", auth: true, body: { mode: "ai", ...options } },
  );
}

export async function classifyAllAdminHospitalActs(
  options: { force?: boolean; limit?: number } = {},
): Promise<{
  classified: number;
  skipped: number;
  hospitals: number;
  ai?: number;
  mode?: string;
  gemini_available?: boolean;
  truncated?: boolean;
  message?: string;
}> {
  return apiRequest(
    "/admin/hospital-acts/classify-all",
    { method: "POST", auth: true, body: { mode: "ai", ...options } },
  );
}

export type AdminAdmissionRow = {
  id: number;
  date: string;
  time: string;
  patient: string;
  patient_sub: string;
  patient_type: "employee" | "beneficiary";
  hospital: string;
  company: string;
  status: "inProgress" | "completed" | "cancelled";
  legacy_status: string;
  motif: string;
  employee_id: string;
};

export async function fetchAdminAdmissions(query: {
  status?: string;
  date_start?: string;
  date_end?: string;
  hospital_id?: number;
  search?: string;
} = {}): Promise<{ total: number; admissions: AdminAdmissionRow[] }> {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.date_start) params.set("date_start", query.date_start);
  if (query.date_end) params.set("date_end", query.date_end);
  if (query.hospital_id) params.set("hospital_id", String(query.hospital_id));
  if (query.search) params.set("search", query.search);
  const qs = params.toString();
  return apiRequest<{ total: number; admissions: AdminAdmissionRow[] }>(
    `/admin/admissions${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function fetchAdminAdmission(id: number): Promise<import("./types").CompanyAdmissionDetail> {
  const response = await apiRequest<{ admission: import("./types").CompanyAdmissionDetail }>(
    `/admin/admissions/${id}`,
    { auth: true },
  );
  return response.admission;
}

export async function updateAdminHospitalActCategory(
  hospitalId: number,
  id: number,
  payload: { action: "confirm" | "correct"; care_category?: string },
): Promise<AdminActsReviewAct> {
  const response = await apiRequest<{ medical_act: AdminActsReviewAct; message?: string }>(
    `/admin/hospital-acts/${hospitalId}/acts/${id}/category`,
    { method: "PATCH", auth: true, body: payload },
  );
  return response.medical_act;
}

export type AdminCompanyBillingAttachment = {
  id: number;
  statement_id: number;
  original_name: string;
  mime_type: string;
  file_size: number;
  created_at: string | null;
};

export type AdminCompanyBillingStatement = {
  id: number;
  company_id: number;
  company_name: string;
  provider_id: number | null;
  provider_name: string;
  hospital_id: number | null;
  hospital_name: string;
  reference_no: string | null;
  label: string;
  nature_facture: string;
  periode_debut: string | null;
  periode_fin: string | null;
  nb_familles: number | null;
  tarif_unitaire_usd: number;
  montant_usd: number;
  description: string | null;
  status: string;
  internal_invoice_id: number | null;
  published_at: string | null;
  created_at: string | null;
  attachments?: AdminCompanyBillingAttachment[];
};

export type AdminCompanyBillingPayload = {
  company_id: number;
  label: string;
  montant_usd: number;
  nature_facture?: "forfait_mensuel" | "hors_forfait";
  provider_id?: number;
  hospital_id?: number;
  reference_no?: string;
  periode_debut?: string;
  periode_fin?: string;
  nb_familles?: number;
  tarif_unitaire_usd?: number;
  description?: string;
  status?: "draft" | "published" | "paid" | "cancelled";
  internal_invoice_id?: number;
};

export async function fetchAdminCompanyBilling(query: {
  company_id?: number;
  status?: string;
} = {}): Promise<{ total: number; statements: AdminCompanyBillingStatement[] }> {
  const params = new URLSearchParams();
  if (query.company_id) params.set("company_id", String(query.company_id));
  if (query.status) params.set("status", query.status);
  const qs = params.toString();
  return apiRequest<{ total: number; statements: AdminCompanyBillingStatement[] }>(
    `/admin/company-billing${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
}

export async function fetchAdminCompanyBillingStatement(
  id: number,
): Promise<AdminCompanyBillingStatement> {
  const response = await apiRequest<{ statement: AdminCompanyBillingStatement }>(
    `/admin/company-billing/${id}`,
    { auth: true },
  );
  return response.statement;
}

export async function createAdminCompanyBilling(
  payload: AdminCompanyBillingPayload,
): Promise<AdminCompanyBillingStatement> {
  const response = await apiRequest<{ statement: AdminCompanyBillingStatement }>(
    "/admin/company-billing",
    { method: "POST", auth: true, body: payload },
  );
  return response.statement;
}

export async function updateAdminCompanyBilling(
  id: number,
  payload: AdminCompanyBillingPayload,
): Promise<AdminCompanyBillingStatement> {
  const response = await apiRequest<{ statement: AdminCompanyBillingStatement }>(
    `/admin/company-billing/${id}`,
    { method: "PUT", auth: true, body: payload },
  );
  return response.statement;
}

export async function publishAdminCompanyBilling(id: number): Promise<AdminCompanyBillingStatement> {
  const response = await apiRequest<{ statement: AdminCompanyBillingStatement }>(
    `/admin/company-billing/${id}/publish`,
    { method: "POST", auth: true },
  );
  return response.statement;
}

export async function uploadAdminCompanyBillingAttachments(
  statementId: number,
  files: File[],
): Promise<{
  uploaded: number;
  errors: string[];
  attachments: AdminCompanyBillingAttachment[];
  message?: string;
}> {
  const form = new FormData();
  for (const file of files) {
    form.append("files[]", file);
  }
  return apiRequest(`/admin/company-billing/${statementId}/attachments`, {
    method: "POST",
    auth: true,
    body: form,
  });
}

export async function deleteAdminCompanyBillingAttachment(attachmentId: number): Promise<void> {
  await apiRequest(`/admin/company-billing/attachments/${attachmentId}`, {
    method: "DELETE",
    auth: true,
  });
}

export async function downloadAdminCompanyBillingAttachment(
  attachmentId: number,
  fileName?: string,
): Promise<void> {
  const { getApiBaseUrl } = await import("@/shared/lib/apiClient");
  const { getAuthToken } = await import("../auth");
  const token = getAuthToken();
  const response = await fetch(
    `${getApiBaseUrl()}/admin/company-billing/attachments/${attachmentId}/download`,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    },
  );
  if (!response.ok) {
    throw new Error("Téléchargement impossible.");
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName || `piece-jointe-${attachmentId}`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export type AdminExchangeRateHospital = {
  hospital_id: number;
  hospital_name: string;
  rate_cdf_per_usd: number | null;
  effective_from: string | null;
  notes: string | null;
};

export type AdminExchangeRateHistory = {
  id: number;
  hospital_id: number;
  rate_cdf_per_usd: number;
  effective_from: string;
  notes: string | null;
  created_at: string | null;
  created_by_name: string | null;
};

export async function fetchAdminExchangeRates(hospitalId?: number): Promise<{
  table_exists: boolean;
  hospitals: AdminExchangeRateHospital[];
  history: AdminExchangeRateHistory[];
}> {
  const params = new URLSearchParams();
  if (hospitalId) params.set("hospital_id", String(hospitalId));
  const qs = params.toString();
  return apiRequest<{
    table_exists: boolean;
    hospitals: AdminExchangeRateHospital[];
    history: AdminExchangeRateHistory[];
  }>(`/admin/exchange-rates${qs ? `?${qs}` : ""}`, { auth: true });
}

export async function createAdminExchangeRate(payload: {
  hospital_id: number;
  rate_cdf_per_usd: number;
  effective_from?: string;
  notes?: string;
}): Promise<void> {
  await apiRequest<{ message: string }>("/admin/exchange-rates", {
    method: "POST",
    auth: true,
    body: payload,
  });
}

export async function fetchAdminInvoices(query: {
  status?: string;
  hospital_id?: number;
  company_id?: number;
  all?: boolean;
} = {}): Promise<import("./types").CompanyBillingInvoice[]> {
  const params = new URLSearchParams();
  if (query.all) params.set("all", "1");
  else if (query.status) params.set("status", query.status);
  if (query.hospital_id) params.set("hospital_id", String(query.hospital_id));
  if (query.company_id) params.set("company_id", String(query.company_id));
  const qs = params.toString();
  const response = await apiRequest<{ invoices: import("./types").CompanyBillingInvoice[] }>(
    `/admin/invoices${qs ? `?${qs}` : ""}`,
    { auth: true },
  );
  return response.invoices ?? [];
}

export async function fetchAdminInvoice(
  id: number,
): Promise<import("./types").CompanyInvoiceDetail> {
  const response = await apiRequest<{ invoice: import("./types").CompanyInvoiceDetail }>(
    `/admin/invoices/${id}`,
    { auth: true },
  );
  return response.invoice;
}

export async function applyAdminInvoiceExchangeRate(
  id: number,
  rateCdfPerUsd?: number | null,
): Promise<import("./types").CompanyInvoiceDetail> {
  const body: Record<string, number> = {};
  if (rateCdfPerUsd != null && rateCdfPerUsd > 0) {
    body.rate_cdf_per_usd = rateCdfPerUsd;
  }
  const response = await apiRequest<{ invoice: import("./types").CompanyInvoiceDetail }>(
    `/admin/invoices/${id}/exchange-rate`,
    { method: "POST", auth: true, body },
  );
  return response.invoice;
}

export type AdminInvoiceUpdatePayload = {
  hospital_id?: number;
  company_id: number;
  convention_id?: number;
  periode_debut: string;
  periode_fin: string;
  nature_facture?: "forfait_mensuel" | "hors_forfait";
  total_amount?: number;
  montant_rssp?: number;
  montant_prise_en_charge?: number;
  reference_prestataire?: string;
  submission_notes?: string;
  invoice_currency?: "USD" | "CDF";
  exchange_rate_cdf?: number;
  forfait_nb_familles?: number;
  forfait_nb_familles_client?: number;
  invoice_file?: File;
};

export async function updateAdminInvoice(
  id: number,
  payload: AdminInvoiceUpdatePayload,
): Promise<import("./types").CompanyInvoiceDetail> {
  const hasFile = payload.invoice_file instanceof File;
  const body: FormData | AdminInvoiceUpdatePayload = hasFile ? new FormData() : { ...payload };

  if (hasFile && body instanceof FormData) {
    if (payload.hospital_id) body.append("hospital_id", String(payload.hospital_id));
    body.append("company_id", String(payload.company_id));
    if (payload.convention_id) body.append("convention_id", String(payload.convention_id));
    body.append("periode_debut", payload.periode_debut);
    body.append("periode_fin", payload.periode_fin);
    if (payload.nature_facture) body.append("nature_facture", payload.nature_facture);
    body.append("total_amount", String(payload.total_amount ?? 0));
    body.append("montant_rssp", String(payload.montant_rssp ?? 0));
    if (payload.montant_prise_en_charge != null) {
      body.append("montant_prise_en_charge", String(payload.montant_prise_en_charge));
    }
    if (payload.reference_prestataire) {
      body.append("reference_prestataire", payload.reference_prestataire);
    }
    if (payload.submission_notes) body.append("submission_notes", payload.submission_notes);
    if (payload.invoice_currency) body.append("invoice_currency", payload.invoice_currency);
    if (payload.exchange_rate_cdf != null) {
      body.append("exchange_rate_cdf", String(payload.exchange_rate_cdf));
    }
    if (payload.forfait_nb_familles) {
      body.append("forfait_nb_familles", String(payload.forfait_nb_familles));
    }
    if (payload.forfait_nb_familles_client) {
      body.append("forfait_nb_familles_client", String(payload.forfait_nb_familles_client));
    }
    body.append("invoice_file", payload.invoice_file as File);
  }

  const response = await apiRequest<{
    invoice: import("./types").CompanyInvoiceDetail;
    message?: string;
  }>(`/admin/invoices/${id}`, {
    method: hasFile ? "POST" : "PUT",
    auth: true,
    body,
  });
  return response.invoice;
}

export type GlobalInvoiceFilters = {
  company_id: number;
  hospital_id?: number;
  provider_id?: number;
  month?: number;
  year?: number;
  start_date?: string;
  end_date?: string;
  system_id?: string;
  act_type?: string;
};

export type GlobalInvoiceResult = {
  generated_at: string;
  period: { start: string; end: string; label: string };
  filters: Record<string, unknown>;
  summary: {
    invoice_count: number;
    total_amount_usd: number;
    total_covered_usd: number;
  };
  invoices: Array<{
    id: number;
    reference: string;
    hospital: string;
    company: string;
    employee: string;
    employee_matricule: string;
    period_start: string;
    period_end: string;
    amount_usd: number;
    covered_usd: number;
    status: string;
    nature: string;
    created_at: string;
  }>;
};

function buildGlobalInvoiceQuery(filters: GlobalInvoiceFilters): string {
  const params = new URLSearchParams();
  params.set("company_id", String(filters.company_id));
  if (filters.hospital_id) params.set("hospital_id", String(filters.hospital_id));
  if (filters.provider_id) params.set("provider_id", String(filters.provider_id));
  if (filters.month) params.set("month", String(filters.month));
  if (filters.year) params.set("year", String(filters.year));
  if (filters.start_date) params.set("start_date", filters.start_date);
  if (filters.end_date) params.set("end_date", filters.end_date);
  if (filters.system_id) params.set("system_id", filters.system_id);
  if (filters.act_type) params.set("act_type", filters.act_type);
  return params.toString();
}

export async function previewGlobalInvoice(
  filters: GlobalInvoiceFilters,
): Promise<GlobalInvoiceResult> {
  const qs = buildGlobalInvoiceQuery(filters);
  return apiRequest<GlobalInvoiceResult>(`/admin/global-invoices?${qs}`, { auth: true });
}

export async function generateGlobalInvoice(
  filters: GlobalInvoiceFilters,
): Promise<GlobalInvoiceResult> {
  const qs = buildGlobalInvoiceQuery(filters);
  const response = await apiRequest<{ global_invoice: GlobalInvoiceResult; message?: string }>(
    `/admin/global-invoices?${qs}`,
    { method: "POST", auth: true },
  );
  return response.global_invoice;
}

export async function exportGlobalInvoiceCsv(filters: GlobalInvoiceFilters): Promise<void> {
  const qs = buildGlobalInvoiceQuery(filters);
  const { getApiBaseUrl } = await import("@/shared/lib/apiClient");
  const { AUTH_TOKEN_KEY } = await import("@/modules/safecheck/auth");
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  const url = `${getApiBaseUrl()}/admin/global-invoices/export?${qs}`;

  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error(`Export impossible (${response.status})`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = `facture-globale-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

export async function fetchAdminInvoiceHospitals(): Promise<
  Array<{ id: number; name: string; provider_id: number }>
> {
  const response = await apiRequest<{
    hospitals: Array<{ id: number; name: string; provider_id: number }>;
  }>("/admin/invoices/hospitals", { auth: true });
  return response.hospitals ?? [];
}

export type AdminPharmacyProduct = {
  id: number;
  provider_id: number;
  code: string;
  label: string;
  unit_price_usd: number;
  status: string;
};

export async function fetchAdminPharmacyMeta(): Promise<{
  providers: Array<{ id: number; name: string; type: string; city: string }>;
}> {
  return apiRequest("/admin/pharmacy/meta", { auth: true });
}

export async function fetchAdminPharmacyProducts(providerId: number): Promise<{
  total: number;
  products: AdminPharmacyProduct[];
}> {
  return apiRequest(`/admin/pharmacy/products?provider_id=${providerId}`, { auth: true });
}

export async function createAdminPharmacyProduct(payload: {
  provider_id: number;
  code: string;
  label: string;
  unit_price_usd: number;
  status?: string;
}): Promise<{ product: AdminPharmacyProduct }> {
  return apiRequest("/admin/pharmacy/products", { method: "POST", auth: true, body: payload });
}

export async function deleteAdminPharmacyProduct(id: number): Promise<{ message: string }> {
  return apiRequest(`/admin/pharmacy/products/${id}`, { method: "DELETE", auth: true });
}
